/**
 * Top-level routing. React Router (HashRouter for simpler PWA install paths —
 * iOS PWAs sometimes mishandle BrowserRouter's path-based navigation when
 * loaded from the home screen).
 *
 * Auth gate: any route except /login requires a session in IndexedDB. The
 * gate redirects to /login when missing.
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import { HashRouter, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { JobIdentity } from './components/JobIdentity.js';
import { getSession } from './db/index.js';
import { reportReauthRequired } from './lib/auth-diag.js';

// Each field workflow is a separate production chunk. This keeps first load
// small on weak mobile connections; Workbox still precaches every generated
// chunk, so all screens remain available after the initial online install.
const LoginScreen = lazy(() =>
  import('./routes/LoginScreen.js').then((module) => ({ default: module.LoginScreen })),
);
const TodaysJobsScreen = lazy(() =>
  import('./routes/TodaysJobsScreen.js').then((module) => ({ default: module.TodaysJobsScreen })),
);
const JobOutcomesGuard = lazy(() =>
  import('./routes/JobOutcomesGuard.js').then((module) => ({
    default: module.JobOutcomesGuard,
  })),
);
const JobWorkspaceScreen = lazy(() =>
  import('./routes/JobWorkspaceScreen.js').then((module) => ({
    default: module.JobWorkspaceScreen,
  })),
);
const ConsentScreen = lazy(() =>
  import('./routes/ConsentScreen.js').then((module) => ({ default: module.ConsentScreen })),
);
const ChecklistScreen = lazy(() =>
  import('./routes/ChecklistScreen.js').then((module) => ({ default: module.ChecklistScreen })),
);
const ClauseScreen = lazy(() =>
  import('./routes/ClauseScreen.js').then((module) => ({ default: module.ClauseScreen })),
);
const RecommendationsScreen = lazy(() =>
  import('./routes/RecommendationsScreen.js').then((module) => ({
    default: module.RecommendationsScreen,
  })),
);
const PhotosScreen = lazy(() =>
  import('./routes/PhotosScreen.js').then((module) => ({ default: module.PhotosScreen })),
);
const ReviewScreen = lazy(() =>
  import('./routes/ReviewScreen.js').then((module) => ({ default: module.ReviewScreen })),
);

export function App() {
  return (
    <HashRouter>
      <Suspense fallback={<ScreenLoader />}>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/" element={<AuthedHome />} />
          <Route path="/jobs/:jobId" element={<AuthedJobLayout />}>
            <Route index element={<JobWorkspaceScreen />} />
            <Route path="cover" element={<RedirectToJobWorkspace />} />
            <Route path="consent" element={<ConsentScreen />} />
            <Route path="hardware" element={<RedirectToJobWorkspace />} />
            <Route path="checklist" element={<ChecklistScreen />} />
            <Route path="checklist/:clauseId" element={<ClauseScreen />} />
            <Route path="recommendations" element={<RecommendationsScreen />} />
            <Route path="photos" element={<PhotosScreen />} />
            <Route path="review" element={<ReviewScreen />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </HashRouter>
  );
}

/** Renders TodaysJobsScreen when authed, redirects to /login otherwise. */
function AuthedHome() {
  return <AuthGate>{() => <TodaysJobsScreen />}</AuthGate>;
}

function AuthedJobLayout() {
  return <AuthGate>{() => <JobOutcomesGuard />}</AuthGate>;
}

/** CER-19: cover/hardware screens are not in the field UI; deep links bounce. */
function RedirectToJobWorkspace() {
  const { jobId = '' } = useParams();
  return <Navigate to={`/jobs/${encodeURIComponent(jobId)}`} replace />;
}

function ScreenLoader() {
  return (
    <div className="screen screen--center">
      <p>Loading…</p>
    </div>
  );
}

function AuthGate({ children }: { children: () => React.ReactElement }) {
  const [state, setState] = useState<'loading' | 'authed' | 'unauthed'>('loading');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    void getSession().then((s) => {
      if (cancelled) return;
      if (s) setState('authed');
      else {
        setState('unauthed');
        reportReauthRequired('no_session_on_boot');
        void navigate('/login', { replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (state === 'loading') {
    return (
      <div className="screen screen--center">
        <p>Loading…</p>
      </div>
    );
  }
  if (state === 'unauthed') return null;
  return (
    <>
      <JobIdentity />
      {children()}
    </>
  );
}
